import type { SentryConfig } from '../config'
import * as Sentry from '@sentry/nextjs'

/**
 * Sentry 核心监控类
 * 根据配置自动启用相应功能
 */
export class SentryMonitor {
  private config: SentryConfig
  private initialized = false

  constructor(config: SentryConfig) {
    this.config = { ...config }
    this.init()
  }

  /**
   * 初始化 Sentry
   */
  private init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return
    }

    try {
      // 基础配置
      const sentryOptions: any = {
        dsn: this.config.dsn,
        environment: this.config.environment,
        release: this.config.release,
        maxBreadcrumbs: 50,
        attachStacktrace: true,
      }

      // 根据配置启用功能
      if (this.config.features.performance) {
        this.setupPerformanceMonitoring(sentryOptions)
      }

      if (this.config.features.sessionReplay) {
        this.setupSessionReplay(sentryOptions)
      }

      // 设置错误过滤
      if (this.config.features.errorMonitoring) {
        this.setupErrorFiltering(sentryOptions)
      }

      // 设置标签和上下文
      this.setupTagsAndContext()

      // 初始化 Sentry
      Sentry.init(sentryOptions)
      this.initialized = true

      // 设置异步错误处理器
      this.setupAsyncErrorHandling()

      console.log('Sentry initialized successfully')
    }
    catch (error) {
      console.error('Failed to initialize Sentry:', error)
    }
  }

  /**
   * 设置性能监控
   */
  private setupPerformanceMonitoring(options: any): void {
    const perf = this.config.performance!

    options.tracesSampleRate = perf.tracesSampleRate
    options.profilesSampleRate = perf.profilesSampleRate

    // 浏览器追踪集成
    options.integrations = options.integrations || []
    options.integrations.push(
      Sentry.browserTracingIntegration({
        enableInp: perf.enableInp,
        enableLongTask: perf.enableLongTask,
      }),
    )

    // 设置页面加载时间监控
    this.setupPageLoadMonitoring()
  }

  /**
   * 设置页面加载时间监控
   */
  private setupPageLoadMonitoring(): void {
    if (typeof window === 'undefined') {
      return
    }

    const handleLoad = () => {
      const loadTime = performance.now()

      // 只在加载时间过长时记录（超过 4 秒）
      if (loadTime > 4000) {
        this.captureMessage('Slow Page Load Detected', 'info')
        this.setContext('page_performance', {
          pageLoadTime: loadTime,
          timestamp: new Date().toISOString(),
          threshold: 4000,
        })
      }
    }

    window.addEventListener('load', handleLoad)
  }

  /**
   * 设置会话回放
   */
  private setupSessionReplay(options: any): void {
    const sessionReplay = this.config.sessionReplay!

    options.replaysSessionSampleRate = sessionReplay.sessionSampleRate
    options.replaysOnErrorSampleRate = sessionReplay.errorSampleRate

    options.integrations = options.integrations || []
    options.integrations.push(
      Sentry.replayIntegration({
        maskAllInputs: sessionReplay.maskAllInputs,
        maskAllText: sessionReplay.maskAllText,
      }),
    )
  }

  /**
   * 设置错误过滤
   */
  private setupErrorFiltering(options: any): void {
    const filter = this.config.errorFilter!

    options.beforeSend = (event: any, hint: any) => {
      const error = hint.originalException

      if (error) {
        // 网络错误过滤
        if (filter.enableNetworkErrorFilter && this.isNetworkError(error)) {
          return null
        }

        // CSP 错误过滤
        if (filter.enableCSPErrorFilter && this.isCSPError(error)) {
          return null
        }

        // Abort 错误过滤
        if (filter.enableAbortErrorFilter && this.isAbortError(error)) {
          return null
        }

        // ResizeObserver 错误过滤
        if (filter.enableResizeObserverFilter && this.isResizeObserverError(error)) {
          return null
        }

        // 自定义过滤器
        if (filter.customFilters) {
          for (const customFilter of filter.customFilters) {
            if (customFilter(error)) {
              return null
            }
          }
        }
      }

      return event
    }
  }

  /**
   * 设置标签和上下文
   */
  private setupTagsAndContext(): void {
    // 设置默认标签
    if (this.config.tags && Object.keys(this.config.tags).length > 0) {
      Object.entries(this.config.tags).forEach(([key, value]) => {
        Sentry.setTag(key, value)
      })
    }

    // 设置默认上下文
    if (this.config.context && Object.keys(this.config.context).length > 0) {
      Object.entries(this.config.context).forEach(([key, value]) => {
        Sentry.setContext(key, value)
      })
    }
  }

  // ====================== 公共 API ======================

  /**
   * 设置用户信息
   */
  setUser(user: { id?: string, email?: string, username?: string }): void {
    if (this.initialized) {
      Sentry.setUser(user)
    }
  }

  /**
   * 设置标签
   */
  setTag(key: string, value: string): void {
    if (this.initialized) {
      Sentry.setTag(key, value)
    }
  }

  /**
   * 设置上下文
   */
  setContext(name: string, context: Record<string, any>): void {
    if (this.initialized) {
      Sentry.setContext(name, context)
    }
  }

  /**
   * 捕获异常
   */
  captureException(error: Error, context?: Record<string, any>): void {
    if (this.initialized) {
      if (context) {
        Sentry.setContext('additional', context)
      }
      Sentry.captureException(error)
    }
  }

  /**
   * 捕获消息
   */
  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    if (this.initialized) {
      Sentry.captureMessage(message, level)
    }
  }

  /**
   * 开始性能追踪
   */
  startSpan<T>(operation: { op: string, name: string }, callback: (span: any) => T): T {
    if (this.initialized && this.config.features.performance) {
      return Sentry.startSpan(operation, callback)
    }
    return callback({})
  }

  /**
   * 记录业务指标
   */
  captureBusinessMetric(name: string, value: number): void {
    if (this.initialized) {
      // 使用 captureMessage 记录业务指标
      this.captureMessage(`Business Metric: ${name} = ${value}`, 'info')
      this.setContext('business_metric', { name, value })
    }
  }

  /**
   * 记录页面性能
   * 注意：现在主要依赖 Sentry 官方的 browserTracingIntegration 进行性能监控
   * 页面加载时间监控已集成到 setupPageLoadMonitoring 方法中
   * 此方法主要用于手动调用场景或补充监控
   */
  capturePagePerformance(metrics: Record<string, number>): void {
    if (this.initialized) {
      this.captureMessage('Page Performance Metrics', 'info')
      this.setContext('page_performance', metrics)
    }
  }

  // ====================== 私有方法 ======================

  private isNetworkError(error: any): boolean {
    return error.name === 'NetworkError'
      || error.message?.includes('Network request failed')
      || error.message?.includes('Failed to fetch')
  }

  private isCSPError(error: any): boolean {
    return error.name === 'SecurityError'
      || error.message?.includes('Content Security Policy')
  }

  private isAbortError(error: any): boolean {
    return error.name === 'AbortError'
      || error.message?.includes('The user aborted a request')
  }

  private isResizeObserverError(error: any): boolean {
    return error.message?.includes('ResizeObserver loop limit exceeded')
  }

  /**
   * 获取配置
   */
  getConfig(): SentryConfig {
    return { ...this.config }
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<SentryConfig>): void {
    this.config = { ...this.config, ...newConfig }
    // 重新初始化
    this.initialized = false
    this.init()
  }

  // ====================== 异步错误处理 ======================

  /**
   * 设置异步错误处理器
   */
  private setupAsyncErrorHandling(): void {
    if (!this.config.features.errorMonitoring) {
      return
    }

    // 1. 未处理的 Promise 拒绝
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this))

    // 2. 全局错误
    window.addEventListener('error', this.handleGlobalError.bind(this))

    // 3. 资源加载错误
    window.addEventListener('error', this.handleResourceError.bind(this), true)
  }

  /**
   * 处理未处理的 Promise 拒绝
   */
  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    this.captureException(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
      context: 'unhandled_promise_rejection',
      reason: event.reason,
      promise: event.promise,
      timestamp: new Date().toISOString(),
    })
  }

  /**
   * 处理全局错误
   */
  private handleGlobalError(event: ErrorEvent): void {
    this.captureException(event.error || new Error(event.message), {
      context: 'global_error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
    })
  }

  /**
   * 处理资源加载错误
   */
  private handleResourceError(event: ErrorEvent): void {
    if (event.target !== window) {
      this.captureException(new Error(`Resource loading failed: ${event.target}`), {
        context: 'resource_error',
        target: event.target,
        filename: event.filename,
        timestamp: new Date().toISOString(),
      })
    }
  }

  /**
   * 包装异步函数，自动捕获错误
   */
  withAsyncErrorHandling<T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    context?: Record<string, any>,
  ): (...args: T) => Promise<R> {
    return async (...args: T): Promise<R> => {
      try {
        return await fn(...args)
      }
      catch (error) {
        this.captureException(error as Error, {
          context: 'async_function_error',
          functionName: fn.name || 'anonymous',
          arguments: args,
          timestamp: new Date().toISOString(),
          ...context,
        })
        throw error
      }
    }
  }

  /**
   * 包装异步函数，静默捕获错误（不抛出）
   */
  withSilentErrorHandling<T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    fallback: R,
    context?: Record<string, any>,
  ): (...args: T) => Promise<R> {
    return async (...args: T): Promise<R> => {
      try {
        return await fn(...args)
      }
      catch (error) {
        this.captureException(error as Error, {
          context: 'async_function_error_silent',
          functionName: fn.name || 'anonymous',
          arguments: args,
          fallback,
          timestamp: new Date().toISOString(),
          ...context,
        })
        return fallback
      }
    }
  }
}
